/* eslint-disable no-param-reassign, no-underscore-dangle, consistent-return */
import axios, { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEnv } from 'src/utils/env.vite';
import { sendNotification } from '../UI/Notification';
import {
  ERR_CODE_400,
  ERR_CODE_401,
  ERR_CODE_403,
  ERR_CODE_500,
  INTERVAL_TIME_120000,
} from '../utils/common/utils';

export const axiosConfig = {
  baseURL: getEnv('VITE_APP_GEN_API_BASE_URL') ?? '',
  timeout: INTERVAL_TIME_120000,
  headers: {},
};

export const GenesisApi = axios.create(axiosConfig);

GenesisApi.interceptors.request.use((config) => {
  const towerPCUserName = sessionStorage.getItem('tower-pc-user');
  const token = sessionStorage.getItem('access_token') ?? '';
  config.headers.Authorization = `Bearer ${token}`;
  if (towerPCUserName) {
    config.headers.TowerPcUserName = towerPCUserName;
  }
  return config;
});

export const getRequestAbortionPieces = () => {
  const abort = axios.CancelToken.source();
  const connectionTimeout = setTimeout(() => {
    abort.cancel(`Connection timeout of ${axiosConfig.timeout}ms.`);
  }, axiosConfig.timeout);

  return { abort, connectionTimeout };
};

export const get = async (
  path: string,
  header?: any
): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getRequestAbortionPieces();
  return GenesisApi.get(path, {
    cancelToken: header?.cancelToken || abort.token,
    responseType: header?.responseType || 'json',
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const post = async (path: string, body: any): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getRequestAbortionPieces();

  return GenesisApi.post(path, body, {
    cancelToken: abort.token,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const patch = async (path: string, body): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getRequestAbortionPieces();

  return GenesisApi.patch(path, body, {
    cancelToken: abort.token,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const put = async (path: string, body: any): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getRequestAbortionPieces();

  return GenesisApi.put(path, body, {
    cancelToken: abort.token,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const deleteApi = async (
  path: string,
  body: any
): Promise<AxiosResponse> => {
  const { connectionTimeout } = getRequestAbortionPieces();
  return GenesisApi.delete(path, { data: body }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const AxiosInterceptor = ({ children }: any) => {
  const navigate = useNavigate();
  const [isSet, setIsSet] = useState(false);

  useEffect(() => {
    const resInterceptor = (response: any) => response;

    const errInterceptor = (err: any) => {
      const error = err?.response || err;
      switch (error?.status) {
        case ERR_CODE_500:
          if (typeof error?.data === 'string') {
            sendNotification({
              type: 'error',
              message: error?.data,
            });
          }
          break;
        case ERR_CODE_400:
          if (typeof error?.data?.title === 'string') {
            sendNotification({
              type: 'error',
              message: error?.data?.title,
            });
          }
          break;
        case ERR_CODE_401:
          if (error.config && !error.config.__isRetryRequest) {
            navigate('/unauthorized-application');
          }
          break;
        case ERR_CODE_403:
          navigate('/forbidden');
          break;
        default:
          break;
      }
      return Promise.reject(
        new Error(error?.statusText || 'An error occurred')
      );
    };
    GenesisApi.interceptors.response.use(resInterceptor, errInterceptor);
    setIsSet(true);
  }, [navigate]);

  return isSet && children;
};
